class OutlookService {
  static getMailboxItem() {
    return Office.context.mailbox.item;
  }

  static getFromEmail() {
    return this.getMailboxItem().from.emailAddress;
  }

  static getFromDisplayName() {
    return this.getMailboxItem().from.displayName;
  }

  static getSubject() {
    return this.getMailboxItem().subject;
  }

  static getDateTimeCreated() {
    return this.getMailboxItem().dateTimeCreated;
  }

  static getItemId() {
    return this.getMailboxItem().itemId;
  }

  static getToEmails() {
    return this.getMailboxItem().to;
  }

  static getCcEmails() {
    return this.getMailboxItem().cc || [];
  }

  static getBccEmails() {
    return [];
  }

  static getAttachments() {
    return this.getMailboxItem().attachments || [];
  }

  static getConversationId() {
    return this.getMailboxItem().conversationId;
  }

  static getInternetMessageId() {
    return this.getMailboxItem().internetMessageId;
  }

  static getImportance() {
    return this.getMailboxItem().importance;
  }

  static getSensitivity() {
    return this.getMailboxItem().sensitivity;
  }

  static getCategories() {
    return this.getMailboxItem().categories || [];
  }

  static getEwsUrl() {
    return Office.context.mailbox.ewsUrl;
  }

  static async getMailBody() {
    return new Promise((resolve, reject) => {
      this.getMailboxItem().body.getAsync(Office.CoercionType.Html, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          resolve(asyncResult.value);
        } else {
          reject(asyncResult.error);
        }
      });
    });
  }

  static async getMailBodyText() {
    return new Promise((resolve, reject) => {
      this.getMailboxItem().body.getAsync(Office.CoercionType.Text, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          resolve(asyncResult.value);
        } else {
          reject(asyncResult.error);
        }
      });
    });
  }

  static async getAttachmentContent(attachmentId) {
    return new Promise((resolve, reject) => {
      this.getMailboxItem().getAttachmentContentAsync(attachmentId, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          resolve(asyncResult.value);
        } else {
          resolve(null);
        }
      });
    });
  }

  static async getAttachmentsWithContent() {
    const attachments = this.getAttachments();
    const attachmentsWithContent = [];

    for (const attachment of attachments) {
      if (attachment.attachmentType === 'file' || attachment.attachmentType === Office.MailboxEnums.AttachmentType.File) {
        try {
          const content = await this.getAttachmentContent(attachment.id);

          if (content) {
            attachmentsWithContent.push({
              id: attachment.id,
              name: attachment.name,
              size: attachment.size,
              attachmentType: attachment.attachmentType,
              contentType: attachment.contentType,
              isInline: attachment.isInline,
              contentId: attachment.contentId || null,
              content: content.content,
              format: content.format
            });
          } else {
            attachmentsWithContent.push({
              id: attachment.id,
              name: attachment.name,
              size: attachment.size,
              attachmentType: attachment.attachmentType,
              contentType: attachment.contentType,
              isInline: attachment.isInline,
              contentId: attachment.contentId || null,
              content: null,
              format: null,
              error: 'Failed to retrieve content'
            });
          }
        } catch (error) {
          attachmentsWithContent.push({
            id: attachment.id,
            name: attachment.name,
            size: attachment.size,
            attachmentType: attachment.attachmentType,
            contentType: attachment.contentType,
            isInline: attachment.isInline,
            contentId: attachment.contentId || null,
            content: null,
            format: null,
            error: error.message
          });
        }
      } else if (attachment.attachmentType === 'item' || attachment.attachmentType === Office.MailboxEnums.AttachmentType.Item) {
        attachmentsWithContent.push({
          id: attachment.id,
          name: attachment.name,
          size: attachment.size,
          attachmentType: 'item',
          contentType: attachment.contentType,
          isInline: attachment.isInline,
          contentId: attachment.contentId || null,
          content: null,
          format: null,
          note: 'Item attachments (embedded emails) are not supported'
        });
      }
    }

    return attachmentsWithContent;
  }

  static convertToLocalClientTime(date) {
    return Office.context.mailbox.convertToLocalClientTime(date);
  }

  static parseDisplayName() {
    const displayName = this.getFromDisplayName();
    const nameParts = displayName.split(' ');
    return {
      firstname: nameParts[0] || '',
      lastname: nameParts[nameParts.length - 1] || '',
      fullname: displayName,
    };
  }

  static async createCompleteEmailArchiveData(parentId, currentUserId) {
    try {
      const rawDateTime = this.getDateTimeCreated();
      const localDateTime = this.convertToLocalClientTime(rawDateTime);
      const formattedDateTime = this.formatDateTimeForAPI(localDateTime);

      const htmlBody = await this.getMailBody();
      const textBody = await this.getMailBodyText();

      const attachmentsWithContent = await this.getAttachmentsWithContent();

      const formatEmailAddresses = (emailArray) => {
        if (!emailArray || !Array.isArray(emailArray)) return [];
        return emailArray.map(email => ({
          emailAddress: email.emailAddress,
          displayName: email.displayName
        }));
      };

      return {
        messageId: this.getItemId(),
        internetMessageId: this.getInternetMessageId(),
        conversationId: this.getConversationId(),
        subject: this.getSubject(),
        dateTimeCreated: formattedDateTime,
        dateTimeCreatedRaw: rawDateTime.toISOString(),

        from: {
          emailAddress: this.getFromEmail(),
          displayName: this.getFromDisplayName()
        },

        to: formatEmailAddresses(this.getToEmails()),
        cc: formatEmailAddresses(this.getCcEmails()),
        bcc: formatEmailAddresses(this.getBccEmails()),

        bodyHtml: htmlBody,
        bodyText: textBody,

        importance: this.getImportance(),
        sensitivity: this.getSensitivity(),
        categories: this.getCategories(),

        attachments: attachmentsWithContent,
        hasAttachments: attachmentsWithContent.length > 0,

        assigned_user_id: currentUserId,
        parent_id: parentId,
        source: 'Outlook Addon',

        ewsUrl: this.getEwsUrl(),
        restUrl: Office.context.mailbox.restUrl,

        from_email: this.getFromEmail(),
        description: htmlBody,
        saved_toid: this.getToEmails()[0]?.emailAddress || '',
        messageid: this.getItemId(),

        createdDate: this.formatDateOnly(localDateTime),
        createdTime: this.formatTimeOnly(localDateTime),
        timestamp: Math.floor(Date.now() / 1000)
      };
    } catch (error) {
      throw error;
    }
  }

  static formatDateTimeForAPI(dateTimeObj) {
    try {
      if (!dateTimeObj) return '';

      if (typeof dateTimeObj === 'string') return dateTimeObj;

      if (dateTimeObj instanceof Date) {
        const year = dateTimeObj.getFullYear();
        const month = String(dateTimeObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateTimeObj.getDate()).padStart(2, '0');
        const hours = String(dateTimeObj.getHours()).padStart(2, '0');
        const minutes = String(dateTimeObj.getMinutes()).padStart(2, '0');
        const seconds = String(dateTimeObj.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      }

      if (dateTimeObj && typeof dateTimeObj === 'object') {
        const date = new Date(
          dateTimeObj.year || dateTimeObj.getFullYear?.() || new Date().getFullYear(),
          (dateTimeObj.month || dateTimeObj.getMonth?.() || 0) - (dateTimeObj.month ? 1 : 0),
          dateTimeObj.day || dateTimeObj.getDate?.() || 1,
          dateTimeObj.hour || dateTimeObj.getHours?.() || 0,
          dateTimeObj.minute || dateTimeObj.getMinutes?.() || 0,
          dateTimeObj.second || dateTimeObj.getSeconds?.() || 0
        );

        return this.formatDateTimeForAPI(date);
      }

      const fallbackDate = new Date(dateTimeObj);
      if (!isNaN(fallbackDate.getTime())) {
        return this.formatDateTimeForAPI(fallbackDate);
      }

      return '';

    } catch (error) {
      return '';
    }
  }

  static formatDateOnly(dateTimeObj) {
    try {
      if (!dateTimeObj) return '';

      const date = dateTimeObj instanceof Date ? dateTimeObj : new Date(dateTimeObj);
      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (error) {
      return '';
    }
  }

  static formatTimeOnly(dateTimeObj) {
    try {
      if (!dateTimeObj) return '';

      const date = dateTimeObj instanceof Date ? dateTimeObj : new Date(dateTimeObj);
      if (isNaN(date.getTime())) return '';

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return '';
    }
  }

  static async createEmailArchiveData(parentId, currentUserId) {
    return this.createCompleteEmailArchiveData(parentId, currentUserId);
  }
}

export default OutlookService;
